import { useEffect, useRef, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { useAccessEpoch } from '@/lib/access-epoch';
import { apiClient } from '@/lib/api-client';

/**
 * What the caller may do in «Note spese» in the active organization (release 1, decision 131):
 * `GET /v1/access/my-permissions?module=expenses`, resolved by the same decision function every
 * route uses. Read by the rail, the route gate and the screens' own actions.
 *
 * ADVISORY, and it FAILS CLOSED: loading and error hold no permission, so nothing is offered until
 * the caller is known to hold it. The finance service re-checks every route.
 */
export type ExpensesPermissionsState =
  { status: 'loading' } | { status: 'ready'; permissions: readonly string[] } | { status: 'error' };

/** Open to the organisation's members (a Richiedente): sees and edits their own expenses. */
export const EXPENSES_SUBMIT = 'expenses:expense:submit';
/** Gestione note spese and Approvatore: see all the organisation's expenses. */
export const EXPENSES_READ_ALL = 'expenses:expense:read-all';
/** Gestione note spese: files and edits on behalf of others («Per conto di», spec §3.1.1). */
export const EXPENSES_MANAGE = 'expenses:expense:manage';
/** The Approvatore: decides on reports and reopens a closed month (release 2). */
export const EXPENSES_APPROVE = 'expenses:report:approve';
/** The Revisore: reads approved reports and the months, writes nothing (release 2, §1.8). */
export const EXPENSES_REPORT_READ = 'expenses:report:read';

export function useExpensesPermissions(): ExpensesPermissionsState {
  const [state, setState] = useState<ExpensesPermissionsState>({ status: 'loading' });
  // Permissions are held per organization: the answer for the previous one stops counting at the
  // switch, the same rule as `useCallerPermissions`.
  const [answeredFor, setAnsweredFor] = useState<string | undefined>(undefined);
  const context = useActiveContextOptional();
  const contextStatus = context?.status;
  const tenantId = context?.active?.tenantId;
  const organizationId = context?.active?.organizationId;
  // Decision 151: a change of access re-reads; the organization already answered keeps its answer
  // on screen meanwhile, and keeps it if the re-read fails.
  const epoch = useAccessEpoch();
  const answeredOrg = useRef<string | undefined>(undefined);

  useEffect(() => {
    const refresh = answeredOrg.current !== undefined && answeredOrg.current === organizationId;
    setAnsweredFor(organizationId);
    // The route resolves permissions in the active organization, so there is nothing to ask until
    // one is chosen; no organization at all holds no permission.
    if (contextStatus === 'loading') {
      setState({ status: 'loading' });
      return;
    }
    if (tenantId === undefined || organizationId === undefined) {
      setState({ status: 'ready', permissions: [] });
      return;
    }
    let active = true;
    if (!refresh) {
      answeredOrg.current = undefined;
      setState({ status: 'loading' });
    }
    apiClient
      .GET('/v1/access/my-permissions', {
        params: {
          query: { module: 'expenses' },
          header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
        },
      })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (error !== undefined || !response.ok || data === undefined) {
          if (!refresh) setState({ status: 'error' });
          return;
        }
        answeredOrg.current = organizationId;
        setState({ status: 'ready', permissions: data.permissions ?? [] });
      })
      .catch(() => {
        if (active && !refresh) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [contextStatus, tenantId, organizationId, epoch]);

  return answeredFor === organizationId ? state : { status: 'loading' };
}

export function holds(state: ExpensesPermissionsState, permissionCode: string): boolean {
  return state.status === 'ready' && state.permissions.includes(permissionCode);
}

/**
 * The Revisore (spec §1.8): reads approved reports and months and downloads, and holds nothing
 * that writes. Whoever also submits, manages or approves is not read-only.
 */
export function isReadOnlyAuditor(state: ExpensesPermissionsState): boolean {
  return (
    holds(state, EXPENSES_REPORT_READ) &&
    !holds(state, EXPENSES_SUBMIT) &&
    !holds(state, EXPENSES_MANAGE) &&
    !holds(state, EXPENSES_APPROVE)
  );
}
